import GuestLayout from "@/components/layout-guest";
import { useGuestData, useSubmitCheckout } from "@/hooks/use-guest";
import { useRoute, useLocation } from "wouter";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { FileUpload } from "@/components/file-upload";
import { Loader2, CheckCircle2, ChevronRight, ChevronLeft, Camera } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/use-language";

// Simple stepper state
type Step = 'info' | 'checklist' | 'photos' | 'fob' | 'confirm';

export default function GuestCheckout() {
  const [match, params] = useRoute("/g/:slug/checkout");
  const slug = params?.slug || "";
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("t");
  const { lang } = useLanguage();
  const [, setLocation] = useLocation();

  const { data, isLoading } = useGuestData(slug, token);
  const submitMutation = useSubmitCheckout();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>('info');
  
  // Form State
  const [formData, setFormData] = useState({
    guestName: "",
    guestPhone: "",
    guestEmail: "",
    fobFrontUrl: "",
    fobBackUrl: "",
  });
  
  const [checklist, setChecklist] = useState<Record<number, boolean>>({});
  const [photoResults, setPhotoResults] = useState<Record<number, string[]>>({});

  if (isLoading || !data) return <div className="p-8 text-center">Loading...</div>;

  const { checklistItems, photoRequirements, apartment } = data;

  const canAdvanceInfo = formData.guestName && formData.guestPhone;
  const canAdvanceChecklist = checklistItems.every(item => !item.isRequired || checklist[item.id]);
  const canAdvancePhotos = photoRequirements.every(req => {
    const count = photoResults[req.id]?.length || 0;
    return !req.isRequired || count >= (req.minPhotos || 1);
  });
  const canAdvanceFob = !apartment.fobRequired || (formData.fobFrontUrl && formData.fobBackUrl);

  const handleSubmit = async () => {
    if (!token) return;
    try {
      await submitMutation.mutateAsync({
        slug,
        data: {
          token,
          ...formData,
          checklistResults: Object.entries(checklist).map(([k, v]) => ({ itemId: Number(k), isChecked: v })),
          photoResults: Object.entries(photoResults).map(([k, v]) => ({ requirementId: Number(k), urls: v })),
        }
      });
      setStep('confirm');
    } catch (err) {
      toast({ 
        title: "Submission Failed", 
        description: "Please check your internet connection and try again.", 
        variant: "destructive" 
      });
    }
  };

  const steps = {
    info: (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold">Your Details</h2>
          <p className="text-muted-foreground">Please provide your contact info.</p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input 
              value={formData.guestName} 
              onChange={e => setFormData(p => ({...p, guestName: e.target.value}))}
              placeholder="John Doe"
              data-testid="input-guest-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone Number *</Label>
            <Input 
              value={formData.guestPhone} 
              onChange={e => setFormData(p => ({...p, guestPhone: e.target.value}))}
              placeholder="+1 234 567 8900"
              data-testid="input-guest-phone"
            />
          </div>
          <div className="space-y-2">
            <Label>Email (Optional)</Label>
            <Input 
              value={formData.guestEmail} 
              onChange={e => setFormData(p => ({...p, guestEmail: e.target.value}))}
              placeholder="john@example.com"
              data-testid="input-guest-email"
            />
          </div>
        </div>
        <Button 
          className="w-full btn-primary h-12" 
          disabled={!canAdvanceInfo}
          onClick={() => setStep('checklist')}
          data-testid="button-next-info"
        >
          Next Step <ChevronRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    ),
    checklist: (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold">Departure Checklist</h2>
          <p className="text-muted-foreground">Please complete these items before leaving.</p>
        </div>
        <div className="space-y-3">
          {checklistItems.map(item => (
            <div key={item.id} className="flex items-start gap-3 p-4 border rounded-xl bg-card">
              <Checkbox 
                id={`item-${item.id}`}
                checked={checklist[item.id] || false}
                onCheckedChange={(checked) => setChecklist(p => ({...p, [item.id]: checked === true}))}
                className="mt-1"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor={`item-${item.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {lang === 'en' ? item.textEn : item.textEs}
                </label>
                {item.isRequired && <p className="text-[10px] text-destructive font-medium uppercase">Required</p>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 h-12" onClick={() => setStep('info')}>Back</Button>
          <Button 
            className="flex-1 btn-primary h-12" 
            disabled={!canAdvanceChecklist}
            onClick={() => setStep('photos')}
          >
            Next Step <ChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    ),
    photos: (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold">Photo Verification</h2>
          <p className="text-muted-foreground">Please take photos of the apartment condition.</p>
        </div>
        <div className="space-y-6">
          {photoRequirements.map(req => (
            <div key={req.id} className="space-y-3">
              <div className="flex justify-between items-baseline">
                <Label className="text-base font-medium">{lang === 'en' ? req.labelEn : req.labelEs}</Label>
                {req.isRequired && <span className="text-xs text-destructive uppercase">Required</span>}
              </div>
              <FileUpload
                label={lang === 'en' ? "Take Photo" : "Tomar Foto"}
                apartmentId={apartment.id}
                onUpload={(url) => {
                  if(url) setPhotoResults(p => ({...p, [req.id]: [...(p[req.id] || []), url]}));
                }}
              />
              <div className="grid grid-cols-3 gap-2">
                {photoResults[req.id]?.map((url, i) => (
                  <img key={i} src={url} className="w-full aspect-square object-cover rounded-md border" />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 h-12" onClick={() => setStep('checklist')}>Back</Button>
          <Button 
            className="flex-1 btn-primary h-12" 
            disabled={!canAdvancePhotos}
            onClick={() => setStep('fob')}
          >
            Next Step <ChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    ),
    fob: (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold">FOB Return</h2>
          <p className="text-muted-foreground">{apartment.fobInstructions}</p>
        </div>
        
        {apartment.fobRequired && (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Photo of FOB (Front) *</Label>
              <FileUpload 
                onUpload={(url) => setFormData(p => ({...p, fobFrontUrl: url}))}
              />
            </div>
            <div className="space-y-3">
              <Label>Photo of FOB (Back/Location) *</Label>
              <FileUpload 
                onUpload={(url) => setFormData(p => ({...p, fobBackUrl: url}))}
              />
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 h-12" onClick={() => setStep('photos')}>Back</Button>
          <Button 
            className="flex-1 btn-primary h-12" 
            disabled={!canAdvanceFob || submitMutation.isPending}
            onClick={handleSubmit}
          >
            {submitMutation.isPending ? <Loader2 className="animate-spin" /> : "Complete Checkout"}
          </Button>
        </div>
      </div>
    ),
    confirm: (
      <div className="flex flex-col items-center justify-center py-12 text-center animate-in zoom-in-95 duration-500">
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-display font-bold mb-2">Checkout Complete!</h2>
        <p className="text-muted-foreground mb-8 max-w-xs mx-auto">
          Thank you for your stay. You have successfully checked out. Safe travels!
        </p>
        <Button variant="outline" onClick={() => window.location.href = `/g/${slug}?t=${token}`}>
          Back to Home
        </Button>
      </div>
    )
  };

  return (
    <GuestLayout slug={slug} title="Checkout" showNav={false}>
      {/* Progress Bar */}
      <div className="flex gap-2 mb-8">
        {['info', 'checklist', 'photos', 'fob'].map((s, i) => {
          const currentIdx = ['info', 'checklist', 'photos', 'fob'].indexOf(step);
          const isActive = i <= currentIdx;
          return (
            <div 
              key={s} 
              className={`h-1.5 flex-1 rounded-full transition-colors ${isActive ? 'bg-primary' : 'bg-muted'}`} 
            />
          );
        })}
      </div>
      
      {steps[step]}
    </GuestLayout>
  );
}
